import apiService from '@sonifi/wb-core/services/api.service';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import {
  cleanHappeningsSlice,
  setHappeningsLoading,
  setLoadedGroupId,
  setTimelineList,
  updateClipSlide,
} from '@sonifi/wb-happenings/store/happeningsSlice';
import {
  getProductsSettings,
  getSettingByProduct,
} from '@sonifi/wb-products/services/products.service';
import { selectTimeline } from './timeline.service';
import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';
import {
  createTimelineListWithOptGroups,
  getImageData,
  getRegionColors,
  getTagsUrls,
  getTimelineListWithLabels,
} from './happeningsHelper';
import {
  BROWSER_ITEM_TYPES,
  MEDIA_KEYS,
  MEDIA_SELECTORS_KEYS,
} from '@sonifi/wb-media/constants/WbMediaConstants';
import {
  getLayouts,
  getMediaById,
  getMediaByTags,
} from '@sonifi/wb-media/services/media.service';
import {
  setHBLayout,
  setSlideSelected,
  setHBLayouts,
  cleanHappeningsSlideEditorSlice,
} from '@sonifi/wb-happenings/store/happeningsSlideEditorSlice';
import { getCanvasImageEmbedded } from '@sonifi/wb-screenbuilder/components/HappeningsBuilder/services/hbScreenBuilder.service';
import { buildApiUri, getIdFromUrl } from '@sonifi/wb-core/utils/parseHelper';
import { setAlert } from '@sonifi/wb-notifier/store/notifierSlice';
import { defaultConfig } from '../../../config-env';
import { PRODUCTS_KEYS } from '@sonifi/wb-products/constants/wbProductsConstants';
import { findParentFromTree } from '@sonifi/wb-media/services/mediaBrowserHelper';
import {
  setBrowserTree,
  setPreviewMedia,
} from '@sonifi/wb-media/store/mediaSlice';

const getChannelsByCurrentGroup = () => async (dispatch, getState) => {
  try {
    const {
      groups: { selectedGroup },
    } = getState();

    const groupUri = selectedGroup.uri;
    const url = `${ENDPOINTS.CHANNELS}?_order_by=title&group=${groupUri}`;
    const res = await apiService.get(url);
    const happeningsChannelSettings = dispatch(
      getSettingByProduct({
        groupId: selectedGroup.id,
        productKey: PRODUCTS_KEYS.HAPPENINGS_CHANNEL,
      }),
    );

    const timelineList = res.data.objects.map((ch) => {
      const group = res.data._embedded[ch.group];
      const audio = res.data._embedded[ch.audio];
      const playlist = ch.playlist.map((item) => {
        const file = res.data._embedded[item.uri];
        return { ...item, ...file, group };
      });
      return { ...ch, audio, group, playlist };
    });

    const timelineListWithLabels = getTimelineListWithLabels({
      timelineList,
      happeningsChannelSettings,
    });

    const timelineListWithOptGroups = createTimelineListWithOptGroups(
      timelineListWithLabels,
      selectedGroup,
    );

    const timelineId = timelineListWithOptGroups[0]?.options[0]?.id ?? null;
    await Promise.all([
      dispatch(
        setTimelineList({
          timelineList,
          timelineListWithOptGroups,
        }),
      ),
      timelineId && dispatch(selectTimeline(timelineId)),
    ]);

    return true;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

const createLayoutRegions = async (sRegions) => {
  const regions = new Map();
  const slideRegions = Object.keys(sRegions);

  for (let index = 0; index < slideRegions.length; index++) {
    const isSelected = index === 0;
    let image = null;

    if (sRegions[slideRegions[index]].content?.image) {
      const imageId = getIdFromUrl({
        url: sRegions[slideRegions[index]].content.image,
      });

      try {
        const res = await apiService.get(`${ENDPOINTS.IMAGES}/${imageId}`);
        image = {
          ...res.data,
          file_uri: res.data._embedded[res.data.files[0]].file_uri,
        };
      } catch (error) {
        console.error(error);
      }
    }

    const currentRegion = {
      ...sRegions[slideRegions[index]],
      content: {
        ...sRegions[slideRegions[index]].content,
        image,
      },
      id: slideRegions[index],
      isSelected,
      ...getRegionColors(sRegions[slideRegions[index]]),
    };
    regions.set(slideRegions[index], currentRegion);
  }

  const objRegions = Object.fromEntries(regions);
  const sortedRegions = Object.keys(objRegions)
    .sort()
    .reduce((result, key) => {
      result[key] = objRegions[key];
      return result;
    }, {});
  return sortedRegions;
};

export const getSlideToEdit = (slideId) => async (dispatch) => {
  try {
    const [slide, layouts] = await Promise.all([
      getMediaById({
        media: MEDIA_KEYS.TYPE.SLIDES.KEY,
        id: slideId,
      }),
      getLayouts(),
    ]);

    const regions = await createLayoutRegions(slide.regions);
    const layout = {
      ...slide._embedded[slide.layout],
      regions,
    };
    const slideMapped = {
      ...slide,
      layout,
    };

    await Promise.all([
      dispatch(setSlideSelected(slideMapped)),
      dispatch(setHBLayouts(layouts)),
      dispatch(setHBLayout(layout)),
    ]);
  } catch (error) {
    console.error(error);
  }
};

export const createSlide = () => async (dispatch) => {
  try {
    const layouts = await getLayouts();
    const defaultLayout = layouts[0];
    const regions = await createLayoutRegions(defaultLayout.regions);
    const layout = {
      ...defaultLayout,
      regions,
    };
    const slide = {
      title: '',
      layout,
    };
    await Promise.all([
      dispatch(setSlideSelected(slide)),
      dispatch(setHBLayouts(layouts)),
      dispatch(setHBLayout(layout)),
    ]);
  } catch (error) {
    console.error(error);
  }
};

export const initHappeningsChannel = (groupId) => async (dispatch) => {
  try {
    dispatch(cleanHappeningsSlice());
    dispatch(setHappeningsLoading(LOADING_STATUS.loading));
    dispatch(setPreviewMedia({ previewMedia: null, mediaSelectorKey: MEDIA_SELECTORS_KEYS.happenings }));
    await dispatch(getProductsSettings(groupId));
    await dispatch(getChannelsByCurrentGroup());
    dispatch(setLoadedGroupId(groupId));
    dispatch(setHappeningsLoading(LOADING_STATUS.success));
  } catch (error) {
    dispatch(setHappeningsLoading(LOADING_STATUS.error));
  }
};

export const saveSlide =
  ({ slide, layout }) =>
  async (dispatch, getState) => {
    try {
      const {
        groups: { selectedGroup },
        media: currentMedia,
      } = getState();
      const regions = {};
      const browserTree =
        currentMedia[MEDIA_SELECTORS_KEYS.happenings].browserTree;
      const folderNode =
        currentMedia[MEDIA_SELECTORS_KEYS.happenings].folderNode;
      const slideNode = browserTree.find((node) => node.data.id === slide.id);

      Object.entries(layout.regions).forEach(([key, reg]) => {
        const currentRegion = new Map(Object.entries(reg));
        currentRegion.delete('isSelected');
        currentRegion.delete('bg');
        currentRegion.delete('id');
        if (reg.content?.image) {
          currentRegion.set('content', {
            ...reg.content,
            image: reg.content.image.uri,
          });
        } else if (reg.content?.text) {
          currentRegion.set('content', {
            text: reg.content.text,
            x: reg.content.x,
            y: reg.content.y,
          });
        } else {
          currentRegion.set('content', null);
        }
        regions[key] = Object.fromEntries(currentRegion);
      });

      const imgData = await getCanvasImageEmbedded(layout);

      const body = {
        files: [],
        group: selectedGroup.uri,
        layout: layout.uri,
        regions,
        title: slide.title,
        embedded_file: imgData,
        tags: getTagsUrls(slide, folderNode.data, slideNode?.data),
      };

      let resSlide = null;
      if (slide.id) {
        resSlide = await apiService.patch(`${ENDPOINTS.SLIDES}/${slide.id}`, {
          ...body,
          created: slide.created,
          id: slide.id,
          uri: slide.uri,
        });
      } else {
        resSlide = await apiService.post(ENDPOINTS.SLIDES, body);
      }

      const { media } = getState();
      const { previewMedia } = media[MEDIA_SELECTORS_KEYS.happenings];

      const slideUpdated = await getMediaById({
        media: MEDIA_KEYS.TYPE.SLIDES.KEY,
        id: resSlide.data.id,
      });
      dispatch(updateClipSlide({ slide: slideUpdated.data }));

      // If the preview media is the slide has been updated, set the preview media to the slide updated
      if (previewMedia?.id === slideUpdated.id) {
       await dispatch(
          setPreviewMedia({
            previewMedia: slideUpdated,
            mediaSelectorKey: MEDIA_SELECTORS_KEYS.happenings,
          }),
        );
      }

      // Finally update the browser tree with the slide and thumbnails
      const newBrowserTree = slide.id ? browserTree.map((item) => {
        if (item.id === slideUpdated.uri) {
          return { ...item, data: slideUpdated };
        }
        return item;
      }) : [
        ...browserTree,
        {
          id: slideUpdated.uri,
          parent: folderNode.id,
          data: slideUpdated,
          type: BROWSER_ITEM_TYPES.MEDIA,
        },
      ];

      await Promise.all([
        dispatch(cleanHappeningsSlideEditorSlice()),
        dispatch(
          setBrowserTree({
            browserTree: newBrowserTree,
            mediaSelectorKey: MEDIA_SELECTORS_KEYS.happenings,
          }),
        ),
      ]);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

export const copySlide =
  ({ slide }) =>
  async (dispatch, getState) => {
    try {
      const {
        groups: { selectedGroup },
        media: currentMedia,
      } = getState();

      const browserTree =
        currentMedia[MEDIA_SELECTORS_KEYS.happenings].browserTree;
      const { parentFolder } = findParentFromTree(browserTree, slide.id);

      const file = await getImageData(slide.file_uri);

      const newSlide = {
        title: `${slide.title} Copy`,
        embedded_file: {
          data: file,
          encoding: 'base64',
          mime: 'image/jpeg',
        },
        regions: slide.regions,
        group: selectedGroup.uri,
        layout: slide.layout,
        tags:
          parentFolder.data.parent_id !== null
            ? [buildApiUri(defaultConfig.apiUri, ENDPOINTS.TAGS, parentFolder.id)]
            : [],
      };

      await apiService.post(ENDPOINTS.SLIDES, newSlide);

      dispatch(
        getMediaByTags({
          media: MEDIA_KEYS.TYPE.SLIDES.KEY,
          groupId: selectedGroup.id,
          mediaSelectorKey: MEDIA_SELECTORS_KEYS.happenings,
          node: parentFolder,
        }),
      );

      return true;
    } catch (error) {
      console.error(error);
      dispatch(
        setAlert({
          content: `An error occurred while copying ${slide.title}. Please try again later.`,
          type: 'danger',
          show: true,
        }),
      );
      return false;
    }
  };
